import { Box, Button } from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useDeferredValue, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CpvCategoryTree } from "./CpvCategoryTree";
import { CpvSearchInput } from "./CpvSearchInput";
import { CpvSearchResults } from "./CpvSearchResults";
import { CpvSelectedChips } from "./CpvSelectedChips";
import { useCpvCodes } from "./useCpvCodes";

export type CpvSelectorProps = {
  value: string[];
  onChange: (next: string[]) => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
};

export const CpvSelector = ({ value, onChange, isDisabled, isInvalid }: CpvSelectorProps) => {
  const { t } = useTranslation();
  const { tree, codeIndex, isLoading } = useCpvCodes();

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const selectedSet = useMemo(() => new Set(value), [value]);

  const handleToggleSelect = (code: string): void => {
    if (selectedSet.has(code)) onChange(value.filter((c) => c !== code));
    else onChange([...value, code]);
  };

  const handleToggleExpand = (code: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleRemove = (code: string): void => {
    onChange(value.filter((c) => c !== code));
  };

  const isSearching = deferredQuery.trim() !== "";

  return (
    <Box>
      <CpvSelectedChips selected={value} codeIndex={codeIndex} onRemove={handleRemove} />
      <CpvSearchInput
        value={query}
        onChange={setQuery}
        isDisabled={isDisabled || isLoading}
        isInvalid={isInvalid}
      />
      <Button
        mt="2"
        variant="link"
        size="sm"
        colorScheme="teal"
        leftIcon={isTreeOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
        onClick={() => setIsTreeOpen((v) => !v)}
        isDisabled={isDisabled || isLoading}
        alignSelf="flex-start"
      >
        {t("Browse categories")}
      </Button>
      {isSearching ? (
        <Box mt="2">
          <CpvSearchResults
            query={deferredQuery}
            tree={tree}
            selected={selectedSet}
            onToggleSelect={handleToggleSelect}
          />
        </Box>
      ) : (
        isTreeOpen && (
          <Box
            mt="2"
            borderWidth="1px"
            borderColor="smBorder"
            borderRadius="md"
            p="2"
            bg="smBackground"
            maxH="24rem"
            overflowY="auto"
          >
            <CpvCategoryTree
              nodes={tree}
              selected={selectedSet}
              expandedIds={expandedIds}
              onToggleSelect={handleToggleSelect}
              onToggleExpand={handleToggleExpand}
            />
          </Box>
        )
      )}
    </Box>
  );
};
